/**
 * Charting stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared
 * dashboard shell (which supplies AppNav + TitleBar). Renders per-relay
 * charting cards (dashboard RelayCard shell with a contract panel and a
 * calendar-visibility toggle) and the relay-load contract calendar for the
 * site's relay controller, sourced from the layout's odl-app unit snapshot.
 *
 * Contains:
 * - LoadVisibilityToggleSwitch — per-relay "show in calendar" switch
 * - ChartingContent — loaded-state body (cards grid + contract calendar)
 * - ChartingView — route child component (default export)
 */
import { Box, FormControlLabel, Paper, Switch } from '@mui/material';
import React, { useContext, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ScopeContext from '@sophocles/app/ScopeContext';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { selectDefaultLoadTypeIdForCustomerType, selectRelayLoadTypes } from '@sophocles/features/reference';
import { selectDisableThirdPartyApi } from '@sophocles/features/tenant/slice';
import { resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';
import ChartingCalendar from '@sophocles/views/sites/components/ChartingCalendar';
import ChartingRelayCard from '@sophocles/views/sites/components/ChartingRelayCard';
import DashboardCard from '@sophocles/views/sites/components/SiteDashboard/DashboardCard';
import { resolveRelayLoadType } from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';
import DashboardSection from '@sophocles/views/sites/components/SiteDashboard/DashboardSection';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

function LoadVisibilityToggleSwitch({ relay, visibleLoads, setVisibleLoads, t }) {
  const relayLoadIds = (relay.loads ?? []).map(load => load.id);
  const allVisible = relayLoadIds.every(id => visibleLoads.includes(id));

  const handleToggle = e => {
    if (e.target.checked) {
      setVisibleLoads(prev => [...new Set([...prev, ...relayLoadIds])]);
    } else {
      setVisibleLoads(prev => prev.filter(id => !relayLoadIds.includes(id)));
    }
  };

  if (relayLoadIds.length === 0) return null;

  return (
    <FormControlLabel
      control={<Switch checked={allVisible} onChange={handleToggle} size="small" />}
      label={t('charting.show')}
      sx={{
        marginRight: 0,
        '& .MuiFormControlLabel-label': {
          color: 'text.secondary',
          fontSize: 12,
        },
      }}
    />
  );
}

// Mounted only once the layout snapshot has loaded, so load-derived state
// (visibleLoads, the contract fetch) initializes from the real load list.
function ChartingContent({ unit, site, siteId, params, siteIccid, refetch }) {
  const { t } = useTranslation();

  const loadTypes = useSelector(selectRelayLoadTypes);
  const loadTypeById = useMemo(() => new Map(loadTypes.map(loadType => [loadType.id, loadType])), [loadTypes]);

  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(scope);
  const defaultLoadTypeId = useSelector(selectDefaultLoadTypeIdForCustomerType(customerTypeId));

  const loadIds = useMemo(() => {
    return (unit?.relays ?? []).flatMap(relay => (relay.loads ?? []).map(load => load.id));
  }, [unit]);

  // Resolve load_type_id → the reference load-type row (for the header chip),
  // falling back to the scope customer type's default when unset, and tag each
  // contract with the load carrying it, so the flat calendar list can still
  // say which load a contract belongs to.
  const relays = useMemo(() => {
    return (unit?.relays ?? []).map(relay => ({
      ...relay,
      load_type: resolveRelayLoadType(relay, loadTypeById, defaultLoadTypeId),
      loads: (relay.loads ?? []).map(load => ({
        ...load,
        contracts: (load.contracts ?? []).map(contract => ({ ...contract, relay_load: load.id })),
      })),
    }));
  }, [unit, loadTypeById, defaultLoadTypeId]);

  const contracts = useMemo(
    () => relays.flatMap(relay => (relay.loads ?? []).flatMap(load => load.contracts ?? [])),
    [relays],
  );

  const [visibleLoads, setVisibleLoads] = useState(loadIds);

  return (
    <Box sx={{ pb: 2 }}>
      <DashboardSection>
        {relays.map(relay => (
          <DashboardCard key={relay.id} span={1} sx={{ alignSelf: 'flex-start' }}>
            <ChartingRelayCard
              relay={relay}
              siteId={siteId}
              unit={unit}
              siteTimeZone={site?.time_zone ?? 'UTC'}
              params={params}
              siteIccid={siteIccid}
              onContractsChanged={refetch}
              headerExtras={
                <LoadVisibilityToggleSwitch
                  relay={relay}
                  visibleLoads={visibleLoads}
                  setVisibleLoads={setVisibleLoads}
                  t={t}
                />
              }
            />
          </DashboardCard>
        ))}
      </DashboardSection>
      <Paper variant="outlined" sx={{ mt: 1.5, p: 2, borderRadius: 2 }}>
        <ChartingCalendar
          contracts={contracts}
          localTimeZone={site?.time_zone}
          visibleLoads={visibleLoads}
          onContractsChanged={refetch}
        />
      </Paper>
    </Box>
  );
}

function ChartingView() {
  const { unit, site, siteId, params, siteIccid, loading, refetch } = useSiteDashboardLayoutContext();
  const disableThirdPartyApi = useSelector(selectDisableThirdPartyApi);

  if (disableThirdPartyApi) {
    return null;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <ChartingContent unit={unit} site={site} siteId={siteId} params={params} siteIccid={siteIccid} refetch={refetch} />
  );
}

export default ChartingView;
